import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Music2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { NAV_KEYS } from './Navbar';

const FOOTER_COPY = {
  tr: { rights: 'Tum haklari saklidir.', quickLinks: 'Hizli Linkler', contact: 'Iletisim',
        certifications: 'Sertifikalar ve Akreditasyonlar',
        nav: ['Ana Sayfa', 'Hakkimizda', 'Hizmetler', 'Paketler', 'Galeri', 'Fiyatlar', 'Iletisim'] },
  en: { rights: 'All rights reserved.', quickLinks: 'Quick Links', contact: 'Contact',
        certifications: 'Certifications & Accreditations',
        nav: ['Home', 'About', 'Services', 'Packages', 'Gallery', 'Pricing', 'Contact'] },
  ru: { rights: 'Все права защищены.', quickLinks: 'Быстрые ссылки', contact: 'Контакты',
        certifications: 'Сертификаты и аккредитации',
        nav: ['Главная', 'О нас', 'Услуги', 'Пакеты', 'Галерея', 'Цены', 'Контакты'] },
  no: { rights: 'Alle rettigheter forbeholdt.', quickLinks: 'Hurtiglenker', contact: 'Kontakt',
        certifications: 'Sertifiseringer og akkrediteringer',
        nav: ['Hjem', 'Om oss', 'Tjenester', 'Pakker', 'Galleri', 'Priser', 'Kontakt'] },
  de: { rights: 'Alle Rechte vorbehalten.', quickLinks: 'Schnelllinks', contact: 'Kontakt',
        certifications: 'Zertifizierungen und Akkreditierungen',
        nav: ['Startseite', 'Über uns', 'Leistungen', 'Pakete', 'Galerie', 'Preise', 'Kontakt'] },
};

// Trust badges / certifications shown in the footer. Add more entries here to grow
// the strip; everywhere the Footer is used will pick them up automatically.
const CERTIFICATIONS = [
  {
    src: '/images/certifications/heart-of-health.svg',
    alt: 'Heart of Health',
    href: null,
  },
];

// Quick Links exclude `contact` because the footer already has a dedicated
// "Contact" column right next to it, so showing it twice would be redundant.
const QUICK_LINK_KEYS = NAV_KEYS.filter((key) => key !== 'contact');

// Mirror of Navbar.NAV_TARGETS so footer links stay in sync. Pricing -> /packages.
const NAV_TARGETS = {
  home: { type: 'anchor', value: 'home' },
  about: { type: 'route', value: '/about' },
  services: { type: 'route', value: '/services' },
  packages: { type: 'route', value: '/packages' },
  gallery: { type: 'anchor', value: 'gallery' },
  pricing: { type: 'route', value: '/packages' },
  contact: { type: 'route', value: '/contact' },
};

const Footer = ({ clinic }) => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const t = FOOTER_COPY[currentLanguage] || FOOTER_COPY.en;

  const phoneTel = String(clinic?.clinic_phone || '').replace(/\s+/g, '');
  const waNumber = String(clinic?.clinic_whatsapp || '').replace(/[^\d]/g, '');

  const renderNavLink = (id, label) => {
    const target = NAV_TARGETS[id];
    if (!target) return null;
    if (target.type === 'route') {
      return <Link key={id} to={target.value}>{label}</Link>;
    }
    const href = isHome ? `#${target.value}` : `/#${target.value}`;
    return <a key={id} href={href}>{label}</a>;
  };

  return (
    <footer className="footer-premium">
      <div className="container-premium footer-grid">
        <div>
          <Link to="/" className="brand footer-brand">
            <img src="/images/logo.png" alt="Crystal Dental" className="logo-img" />
            <span>Crystal Dental</span>
          </Link>
          {clinic?.clinic_address ? (
            <p className="footer-muted">{clinic.clinic_address}</p>
          ) : null}
          <div className="social-row">
            <a href="https://instagram.com/crystaldentalalanya" target="_blank" rel="noreferrer" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a href="https://facebook.com/crystaldentalalanya" target="_blank" rel="noreferrer" aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <a href="#" aria-label="YouTube"><Youtube size={18} /></a>
            <a href="#" aria-label="TikTok"><Music2 size={18} /></a>
          </div>

          {CERTIFICATIONS.length > 0 && (
            <div className="footer-certs">
              <h4 className="footer-certs-title">{t.certifications}</h4>
              <div className="footer-certs-row">
                {CERTIFICATIONS.map((cert) => {
                  const img = (
                    <img
                      key={cert.src}
                      src={cert.src}
                      alt={cert.alt}
                      className="footer-cert-logo"
                      loading="lazy"
                    />
                  );
                  return cert.href ? (
                    <a
                      key={cert.src}
                      href={cert.href}
                      target="_blank"
                      rel="noreferrer"
                      className="footer-cert-link"
                      aria-label={cert.alt}
                    >
                      {img}
                    </a>
                  ) : (
                    img
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="footer-title">{t.quickLinks}</h4>
          <div className="footer-links footer-links-2col">
            {QUICK_LINK_KEYS.map((id) => {
              const idx = NAV_KEYS.indexOf(id);
              return renderNavLink(id, t.nav[idx]);
            })}
          </div>
        </div>

        <div>
          <h4 className="footer-title">{t.contact}</h4>
          <div className="footer-links">
            {phoneTel && <a href={`tel:${phoneTel}`}>{clinic.clinic_phone}</a>}
            {waNumber && (
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="container-premium footer-row">
        <p>© {new Date().getFullYear()} Crystal Dental Alanya. {t.rights}</p>
      </div>
    </footer>
  );
};

export default Footer;
